import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const VoiceActorStatus = ({ voiceActors, onNavigateToAssignments }) => {
  const [sortBy, setSortBy] = useState('utilization');

  const sortedActors = [...voiceActors]?.sort((a, b) => {
    if (sortBy === 'utilization') return b?.utilizationRate - a?.utilizationRate;
    if (sortBy === 'assignments') return b?.totalAssignments - a?.totalAssignments;
    if (sortBy === 'lines') return b?.totalLines - a?.totalLines;
    if (sortBy === 'name') return a?.stageName?.localeCompare(b?.stageName);
    return 0;
  });

  const getUtilizationColor = (rate) => {
    if (rate >= 80) return 'text-success';
    if (rate >= 50) return 'text-warning';
    return 'text-error';
  };

  const getAvailabilityBadge = (isAvailable) => {
    return isAvailable
      ? 'bg-success/10 border-success text-success' :'bg-muted/10 border-border text-muted-foreground';
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-card-foreground">Voice Actor Assignment Status</h2>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <label className="text-sm text-muted-foreground">Sort by:</label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e?.target?.value)}
              className="px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="utilization">Utilization Rate</option>
              <option value="assignments">Total Assignments</option>
              <option value="lines">Total Lines</option>
              <option value="name">Name</option>
            </select>
          </div>
          <button
            onClick={onNavigateToAssignments}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors flex items-center gap-2"
          >
            <Icon name="UserPlus" size={16} />
            Manage Assignments
          </button>
        </div>
      </div>
      {voiceActors?.length === 0 ? (
        <div className="text-center py-12">
          <Icon name="Users" size={48} className="text-muted-foreground mx-auto mb-4" />
          <p className="text-lg font-semibold text-card-foreground mb-2">No Voice Actors Found</p>
          <p className="text-muted-foreground">Add voice actors to start assigning recordings.</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-3 px-4 text-sm font-semibold text-muted-foreground">Voice Actor</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-muted-foreground">Status</th>
                <th className="text-center py-3 px-4 text-sm font-semibold text-muted-foreground">Total Assignments</th>
                <th className="text-center py-3 px-4 text-sm font-semibold text-muted-foreground">Completed</th>
                <th className="text-center py-3 px-4 text-sm font-semibold text-muted-foreground">In Progress</th>
                <th className="text-center py-3 px-4 text-sm font-semibold text-muted-foreground">Pending</th>
                <th className="text-center py-3 px-4 text-sm font-semibold text-muted-foreground">Total Lines</th>
                <th className="text-center py-3 px-4 text-sm font-semibold text-muted-foreground">Utilization</th>
              </tr>
            </thead>
            <tbody>
              {sortedActors?.map((actor, index) => (
                <tr key={actor?.id} className="border-b border-border hover:bg-muted/50 transition-colors">
                  <td className="py-4 px-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                        <Icon name="User" size={20} className="text-primary" />
                      </div>
                      <div>
                        <p className="font-semibold text-card-foreground">{actor?.stageName}</p>
                        <p className="text-xs text-muted-foreground">{actor?.vocalRange || 'N/A'}</p>
                      </div>
                    </div>
                  </td>
                  <td className="py-4 px-4">
                    <span className={`px-2 py-1 rounded-full border text-xs font-medium ${getAvailabilityBadge(actor?.isAvailable)}`}>
                      {actor?.isAvailable ? 'Available' : 'Unavailable'}
                    </span>
                  </td>
                  <td className="py-4 px-4 text-center">
                    <span className="font-semibold text-card-foreground">{actor?.totalAssignments}</span>
                  </td>
                  <td className="py-4 px-4 text-center">
                    <span className="text-success font-medium">{actor?.completedAssignments}</span>
                  </td>
                  <td className="py-4 px-4 text-center">
                    <span className="text-warning font-medium">{actor?.inProgressAssignments}</span>
                  </td>
                  <td className="py-4 px-4 text-center">
                    <span className="text-muted-foreground font-medium">{actor?.pendingAssignments}</span>
                  </td>
                  <td className="py-4 px-4 text-center">
                    <span className="font-semibold text-card-foreground">{actor?.totalLines}</span>
                  </td>
                  <td className="py-4 px-4 text-center">
                    <div className="flex items-center justify-center gap-2">
                      <span className={`font-semibold ${getUtilizationColor(actor?.utilizationRate)}`}>
                        {actor?.utilizationRate}%
                      </span>
                      <div className="w-16 bg-muted rounded-full h-2">
                        <div
                          className={`h-2 rounded-full transition-all ${
                            actor?.utilizationRate >= 80
                              ? 'bg-success'
                              : actor?.utilizationRate >= 50
                              ? 'bg-warning' :'bg-error'
                          }`}
                          style={{ width: `${actor?.utilizationRate}%` }}
                        ></div>
                      </div>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default VoiceActorStatus;